import { filter } from "lodash";
import { DateHelper } from "../../../../../../../../react-core/Helpers/DateHelper";
import { addDays, addYears } from "../../../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../../../react-core/Utils/JsonDateParser";
import { Frequency } from "../../../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { PensionArrangementType } from "../../../../../../../../Wrap/Constants/Enums/Domain/PensionArrangementType";
import { SourceType } from "../../../../../../../../Wrap/Constants/Enums/Domain/SourceType";
import { SubAccountType } from "../../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { DrawdownType } from "../../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { MoneyOrPercentageType } from "../../../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { CappedModel } from "../../../../../../../../Wrap/Models/Domains/Drawdown/CappedModel";
import { DrawdownAnnualIncome } from "../../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualIncome";
import { DrawdownIncomeAmendmentInstruction } from "../../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownIncomeAmendmentInstruction";
import { FlexiModel } from "../../../../../../../../Wrap/Models/Domains/Drawdown/FlexiModel";
import { PensionProtectionDetails } from "../../../../../../../../Wrap/Models/Models/Customer/PensionProtection/PensionProtectionDetails";
import { PensionTransfer } from "../../../../../../../../Wrap/Models/Models/Processes/Quote/Pension/PensionTransfer";
import { PensionBase } from "../../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { DrawdownModel } from "../../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { IJsonDate } from "../../../../../../../../Wrap/System/IJsonDate";
import { MoneyHelper } from "../../../../../../../Shared/Common/MoneyHelper";
import { ProcessType } from "../../../../../../../Shared/IST/Resource/Content";
import { Process } from "../../../../../../../Shared/Process/Process/Process";
import {
    nbSippIncomePaymentDetailsLabels,
    nextYearsGadMaximum,
    TransferOrDrawdown,
} from "../../../../NewBusinessWizard/Resource/Contents";
import { ChargesHelper } from "../../../Helpers/ChargesHelper";
import { processTypeEnum } from "../../../Resource/Contents";
import {
    calculateNetDrawdownValue,
    totalCashValue,
    totalReRegValue,
} from "../../DrawdownDetails/Helper/DrawdownDetailHelper";

export const shouldDisplayDifferentInitialIncomePayment = (
    data: DrawdownAnnualIncome,
    drawdownType: DrawdownType,
    pensionTransfer: PensionTransfer[],
    hasCappedArrangement: boolean,
    processType: processTypeEnum,
    regularCrystalDifferentInital: boolean
): boolean => {
    if (drawdownType === DrawdownType.Regular) {return regularCrystalDifferentInital;}
    return (
        data?.TargetSpecificArrangements != true &&
        !hasCappedArrangement &&
        drawdownType !== DrawdownType.Capped &&
        processType !== processTypeEnum.GadReview &&
        !pensionTransfer?.find((transfer) => {
            return transfer.DrawdownType === DrawdownType.Capped;
        })
    );
};

export const isIncomeExhausted = (
    drawdownType: DrawdownType,
    data: DrawdownAnnualIncome,
    pensionBase: PensionBase,
    processType: processTypeEnum,
    isITFC: boolean
): boolean => {
    let paymentsPerYear = 0;
    if (data?.Frequency) {
        switch (data?.Frequency) {
            case Frequency.Monthly:
                paymentsPerYear = 12;
                break;
            case Frequency.Quarterly:
                paymentsPerYear = 4;
                break;
            case Frequency.HalfYearly:
                paymentsPerYear = 2;
                break;
            case Frequency.Yearly:
                paymentsPerYear = 1;
                break;
        }
    }
    if (
        drawdownType == DrawdownType.Flexible &&
        data?.GrossIncomePerPayment?.Amount &&
        processType != processTypeEnum.Quote
    ) {
        const incomePerYear = MoneyHelper.roundNumberToDecimalPlaces(
            MoneyHelper.multiplyBy(data?.GrossIncomePerPayment?.Amount, paymentsPerYear).Amount,
            2
        );
        let maximumIncomePerPayment = 0;
        if (data?.TargetSpecificArrangements) {
            // use target algorithm
            maximumIncomePerPayment = calculateMaximumIncomePerPaymentWithTargetAlgorithm(
                processType,
                pensionBase,
                isITFC
            );
        } else {
            // use standard algorithm
            maximumIncomePerPayment = calculateMaximumIncomePerPaymentWithStandardAlgorithm(
                processType,
                pensionBase,
                isITFC
            );
        }
        return incomePerYear > maximumIncomePerPayment;
    }
    return false;
};

export const getAssetsToSellTotalForIncome = (data: DrawdownModel): number => {
    return data?.Income?.TargetSpecificArrangements
        ? data?.Income?.GrossIncomePerPaymentFromPension?.Amount
        : data?.Income?.GrossIncomePerPayment?.Amount || 0;
};

export const getAssetsToSellTotalForIncomeFromIncome = (data: DrawdownAnnualIncome): number => {
    return data?.TargetSpecificArrangements
        ? data?.GrossIncomePerPaymentFromPension?.Amount
        : data?.GrossIncomePerPayment?.Amount || 0;
};

export const calculateMaximumAvailablePerPayment = (
    processType: processTypeEnum | ProcessType,
    drawdown: DrawdownModel
): number => {
    if (
        processType == processTypeEnum.Quote ||
        processType == ProcessType.Quote ||
        processType == processTypeEnum.NewBusiness ||
        processType == ProcessType.NB
    ) {
        const returnValue =
            (drawdown?.RegularCrystallisationDetails?.Amount?.Amount || 0) - (drawdown?.TaxFreeLumpSum?.Amount || 0);
        return returnValue < 0 ? 0 : returnValue;
    }
    return 0;
};

export const calculateMaximumIncomePerPaymentWithStandardAlgorithm = (
    processType: processTypeEnum | ProcessType,
    pensionBase: PensionBase,
    isITFC: boolean,
    existingArrangements: DrawdownIncomeAmendmentInstruction[] = []
): number => {
    if (
        processType == processTypeEnum.Quote ||
        processType == ProcessType.Quote ||
        processType == processTypeEnum.NewBusiness ||
        processType == ProcessType.NB
    ) {
        const crystallisedTransfer = pensionBase?.PensionTransfers?.filter(
            (e) => e?.PensionSource == PensionArrangementType.Crystallised
        );
        if (hasCappedTransfer(crystallisedTransfer || [])) {
            // Scenario 1 - have capped transfer THEN IGNORE all flexi arrangements
            // incomePerPayment = SUM(GAD maximum / frequency)
            const returnValue = maximumIncomePerPaymentForCappedTransfers(
                crystallisedTransfer?.filter((e) => e?.DrawdownType == DrawdownType.Capped),
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency
            );
            // round down to 2 decimals
            return Math.floor(returnValue * 100) / 100;
        } else if (
            pensionBase?.DrawdownModel?.DrawdownType == DrawdownType.Flexible ||
            hasFlexiTransferOnly(crystallisedTransfer)
        ) {
            // Scenario 2 - only have flexi drawdown or flexi transfer
            const flexiTransfer = maximumIncomePerPaymentForFlexiTransfers(
                crystallisedTransfer?.filter((e) => e?.DrawdownType == DrawdownType.Flexible),
                true
            );
            const netToDrawdown = calculateNetDrawdownValue(pensionBase?.DrawdownModel, pensionBase?.PensionProtection);
            const iac = calculateIAC(pensionBase, isITFC);
            const diac = calculateDIAC(pensionBase?.DrawdownModel);

            // (((Flexi Transfers+Net to single drawdown) - Initial adviser charges taken from product cash)-SIPP Product charges)
            // TODO Product charges ignore for now
            const returnValue = flexiTransfer + netToDrawdown - iac - diac;
            return returnValue < 0 ? 0 : returnValue;
        }
    } else if (processType == processTypeEnum.topUpWithITFLS || processType == ProcessType.TopUpWithITFLS) {
        const cappedArrangements = filter(existingArrangements, { Type: PensionArrangementType.Capped });
        if (cappedArrangements && cappedArrangements.length > 0) {
            // if have capped arrangement - apply capped formula
            // (SUM (Each GAD Maximum /Frequency))
            let total = 0;
            const frequency = pensionBase?.DrawdownModel?.Income?.Frequency || Frequency.Monthly;
            cappedArrangements.forEach((e) => {
                const value = (e?.CappedArrangement?.NextYearsGadMaximum || 0) / frequencyMap[frequency];
                total += value;
            });
            return total;
        } else {
            // apply flexi formula
            // (((Each value to move to crystallised account - Drawdown initial adviser charge to come from product cash)-SIPP Product Charges) + (value of existing flexi access arrangements - any in progress income payments))
            // Ignore product charge for now
            const netToDrawdown = calculateNetDrawdownValue(pensionBase?.DrawdownModel, pensionBase?.PensionProtection);
            const diac = calculateDIAC(pensionBase?.DrawdownModel);

            // Find all flexi existing arrangements
            const flexiArrangements = filter(existingArrangements, { Type: PensionArrangementType.Fad });
            let flexiTotal = 0;
            if (flexiArrangements && flexiArrangements.length > 0) {
                flexiArrangements.forEach((e) => {
                    const amount = calculateFlexiArrangementAmount(e?.FlexiArrangement);
                    flexiTotal += amount;
                });
            }

            return netToDrawdown - diac + flexiTotal;
        }
    }
    return 0;
};

export const getInitialPaymentRequested = (pensionBase: PensionBase) => {
    return (
        pensionBase?.DrawdownModel?.Income?.GrossInitialIncomePayment?.Amount ||
        (pensionBase?.PensionTransfers &&
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                ?.GrossInitialIncomePayment?.Amount)
    );
};

export const calculateMaximumIncomePerPaymentWithTargetAlgorithm = (
    processType: processTypeEnum | ProcessType,
    pensionBase: PensionBase,
    isITFC: boolean,
    existingArrangements: DrawdownIncomeAmendmentInstruction[] = []
): number => {
    if (
        processType == processTypeEnum.Quote ||
        processType == ProcessType.Quote ||
        processType == processTypeEnum.NewBusiness ||
        processType == ProcessType.NB
    ) {
        // Capped transfer
        // SUM(Each Transfer GAD Maximum)-Each income paid to date) / Pension Year Frequency)
        const crystallisedTransfer = pensionBase?.PensionTransfers?.filter(
            (e) => e?.PensionSource == PensionArrangementType.Crystallised
        );
        const firstPaymentDate =
            pensionBase?.DrawdownModel?.Income?.NextPayment ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.NextPayment;
        const frequency =
            pensionBase?.DrawdownModel?.Income?.Frequency ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency;
        const paymentEndDate =
            pensionBase?.DrawdownModel?.Income?.PaymentEnd ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.PaymentEnd;
        const cappedTransfer = cappedTransferWithTarget(
            crystallisedTransfer?.filter((e) => e?.DrawdownType == DrawdownType.Capped),
            firstPaymentDate,
            paymentEndDate,
            frequency
        );
        const flexiTransfer = maximumIncomePerPaymentForFlexiTransfers(
            crystallisedTransfer?.filter((e) => e?.DrawdownType == DrawdownType.Flexible),
            true
        );
        const netToDrawdown = calculateNetDrawdownValue(pensionBase?.DrawdownModel, pensionBase?.PensionProtection);
        const iac = calculateIAC(pensionBase, isITFC);
        const diac = calculateDIAC(pensionBase?.DrawdownModel);

        // (((SUM(Each Transfer GAD Maximum)-Each income paid to date) / Pension Year Frequency) + (Flexi Transfers+Net to single drawdown) - Initial adviser charge taken from product cash for Flexi)-SIPP Product charges)
        const returnValue = cappedTransfer + flexiTransfer + netToDrawdown - iac - diac;
        return returnValue < 0 ? 0 : returnValue;
    } else if (processType == processTypeEnum.topUpWithITFLS || processType == ProcessType.TopUpWithITFLS) {
        // SUM ((((Each existing GA Maximum - Each income paid to date) - each in progress capped income payments)/ Pension Year Frequency) + (((Each value to move to crystallised account - Drawdown initial adviser charge to come from product cash)-SIPP Product charges) + (value of existing flexi access arrangements - any in progress income payments))).
        // Capped arrangements
        const cappedArrangements = filter(existingArrangements, { Type: PensionArrangementType.Capped });
        let cappedArrangementsValue = 0;
        if (cappedArrangements && cappedArrangements.length > 0) {
            cappedArrangements.forEach((e) => {
                const value = cappedArrangementWithTarget(e?.CappedArrangement, pensionBase?.DrawdownModel?.Income);
                cappedArrangementsValue += value;
            });
        }

        // Flexi arrangements & drawdown
        const netToDrawdown = calculateNetDrawdownValue(pensionBase?.DrawdownModel, pensionBase?.PensionProtection);
        const diac = calculateDIAC(pensionBase?.DrawdownModel);

        // Find all flexi existing arrangements
        const flexiArrangements = filter(existingArrangements, { Type: PensionArrangementType.Fad });
        let flexiTotal = 0;
        if (flexiArrangements && flexiArrangements.length > 0) {
            flexiArrangements.forEach((e) => {
                const amount = calculateFlexiArrangementAmount(e?.FlexiArrangement);
                flexiTotal += amount;
            });
        }

        const flexiArrangementsValue = netToDrawdown - diac + flexiTotal;

        const returnValue = cappedArrangementsValue + flexiArrangementsValue;
        return returnValue < 0 ? 0 : returnValue;
    }
    return 0;
};

export const calculateMaximumInitialIncomePayment = (
    processType: processTypeEnum | ProcessType,
    pensionBase: PensionBase,
    isITFC: boolean,
    existingArrangements: DrawdownIncomeAmendmentInstruction[] = []
): number => {
    if (
        processType == processTypeEnum.Quote ||
        processType == ProcessType.Quote ||
        processType == processTypeEnum.NewBusiness ||
        processType == ProcessType.NB
    ) {
        if (pensionBase?.DrawdownModel?.DrawdownType == DrawdownType.Regular) {
            // Regular drawdown
            // Maximum initial income payment = (Initial Crystallisation amount – Tax free cash for initial crystallisation)
            // TODO - Tax free cash for initial crystallisation need discuss how to handle this field
            return (
                (pensionBase?.DrawdownModel?.RegularCrystallisationDetails?.InitialAmount?.Amount || 0) -
                (pensionBase?.DrawdownModel?.RegularCrystallisationDetails?.InitialAmountPCLS?.Amount || 0)
            );
        } else {
            return calculateMaximumIncomePerPaymentWithStandardAlgorithm(
                processType,
                pensionBase,
                isITFC,
                existingArrangements
            );
        }
    }
    if (processType == processTypeEnum.topUpWithITFLS) {
        const returnValue =
            calculateMaximumIncomePerPaymentWithStandardAlgorithm(
                processType,
                pensionBase,
                isITFC,
                existingArrangements
            );
        return Math.max(0, returnValue);
    }
    return 0;
};

export const calculateMaximumGrossOneOffIncomePaymentWithStandardAlgorithm = (
    processType: processTypeEnum | ProcessType,
    pensionBase: PensionBase,
    isITFC: boolean
): number => {
    if (processType == processTypeEnum.Quote || processType == ProcessType.Quote) {
        const crystallisedTransfer = pensionBase?.PensionTransfers?.filter(
            (e) => e?.PensionSource == PensionArrangementType.Crystallised
        );
        if (hasCappedTransfer(crystallisedTransfer || [])) {
            // (SUM (Each Transfer GAD Maximum - income paid to date in pension year for each arrangement) - (Income requested x frequency))
            const sumedGad = sumGadMaximumForCappedTransfers(
                pensionBase?.PensionTransfers?.filter(
                    (e) =>
                        e?.PensionSource == PensionArrangementType.Crystallised &&
                        e?.DrawdownType == DrawdownType.Capped
                )
            );
            const incomeRequest =
                (pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                    ?.GrossIncomePerPayment?.Amount || 0) *
                frequencyMap[
                    pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency ||
                        Frequency.Monthly
                ];

            const subsequentIncomePaymentsFromPension =
                (pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                    ?.GrossIncomePerPaymentFromPension?.Amount || 0) *
                frequencyMap[
                    pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency ||
                        Frequency.Monthly
                ];
            const returnValue = sumedGad - incomeRequest - subsequentIncomePaymentsFromPension;
            return returnValue < 0 ? 0 : returnValue;
        } else if (
            pensionBase?.DrawdownModel?.DrawdownType == DrawdownType.Flexible ||
            hasFlexiTransferOnly(crystallisedTransfer)
        ) {
            // Flexi only
            // ((Maximum income per payment - income requested for initial income payment) - (income requested for subsequent income payments x balance frequency))
            const maximumIncomePerPayment = calculateMaximumIncomePerPaymentWithStandardAlgorithm(
                processType,
                pensionBase,
                isITFC
            );
            const initialPaymentRequested =
                pensionBase?.DrawdownModel?.Income?.GrossInitialIncomePayment?.Amount ||
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                    ?.GrossInitialIncomePayment?.Amount;
            const subsequentIncomePayments =
                pensionBase?.DrawdownModel?.Income?.GrossIncomePerPayment?.Amount ||
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                    ?.GrossIncomePerPayment?.Amount;
            const subsequentIncomePaymentsFromPension =
                pensionBase?.DrawdownModel?.Income?.GrossIncomePerPaymentFromPension?.Amount ||
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                    ?.GrossIncomePerPaymentFromPension?.Amount;
            const frequency =
                pensionBase?.DrawdownModel?.Income?.Frequency ||
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency;
            const firstPaymentDate =
                pensionBase?.DrawdownModel?.Income?.NextPayment ||
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.NextPayment;
            const paymentEndDate =
                pensionBase?.DrawdownModel?.Income?.PaymentEnd ||
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.PaymentEnd;
            let balanceFrequency = calculateBalanceFrequency(
                JsonDateParser.toJsonDateNoOffset(new Date()),
                firstPaymentDate,
                paymentEndDate,
                frequency
            );
            balanceFrequency = initialPaymentRequested > 0 ? balanceFrequency - 1 : balanceFrequency;
            const returnValue =
                maximumIncomePerPayment -
                (initialPaymentRequested || 0) -
                (subsequentIncomePayments || 0) * balanceFrequency -
                (subsequentIncomePaymentsFromPension || 0) * balanceFrequency;

            return returnValue < 0 ? 0 : returnValue;
        }
    }
    return 0;
};

export const calculateMaximumGrossOneOffIncomePaymentWithTargetAlgorithm = (
    processType: processTypeEnum | ProcessType,
    pensionBase: PensionBase,
    isITFC: boolean
): number => {
    // Maximum income payment
    const maximumIncomePerPayment = calculateMaximumIncomePerPaymentWithTargetAlgorithm(
        processType,
        pensionBase,
        isITFC
    );
    const subsequentIncomePayments =
        pensionBase?.DrawdownModel?.Income?.GrossIncomePerPayment?.Amount ||
        pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.GrossIncomePerPayment
            ?.Amount;
    const subsequentIncomePaymentsFromPension =
        pensionBase?.DrawdownModel?.Income?.GrossIncomePerPaymentFromPension?.Amount ||
        pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
            ?.GrossIncomePerPaymentFromPension?.Amount;
    const frequency =
        pensionBase?.DrawdownModel?.Income?.Frequency ||
        pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency;
    const firstPaymentDate =
        pensionBase?.DrawdownModel?.Income?.NextPayment ||
        pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.NextPayment;
    const paymentEndDate =
        pensionBase?.DrawdownModel?.Income?.PaymentEnd ||
        pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.PaymentEnd;
    const balanceFrequency = calculateBalanceFrequency(
        JsonDateParser.toJsonDateNoOffset(new Date()),
        firstPaymentDate,
        paymentEndDate,
        frequency
    );

    const returnValue =
        maximumIncomePerPayment -
        (subsequentIncomePayments || 0) * balanceFrequency -
        (subsequentIncomePaymentsFromPension || 0) * balanceFrequency;
    return returnValue < 0 ? 0 : returnValue;
};

export const calculateMaximumIncomePaymentForRemainderOfYear = (
    processType: processTypeEnum | ProcessType,
    transferOrDrawdown: TransferOrDrawdown,
    pensionBase: PensionBase
): number => {
    if (processType == processTypeEnum.NewBusiness || processType == ProcessType.NB) {
        // ((GAD Maximum - Income Paid to date) / Pension Year Frequency)
        const gadMaximum = transferOrDrawdown?.IncomePaymentDetails?.GadMaximum?.Amount || 0;
        const incomePaidToDate = transferOrDrawdown?.IncomePaymentDetails?.CurrentYearIncome?.Amount || 0;
        const frequency =
            pensionBase?.DrawdownModel?.Income?.Frequency ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency;
        const firstPaymentDate =
            pensionBase?.DrawdownModel?.Income?.NextPayment ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.NextPayment;
        const paymentEndDate =
            pensionBase?.DrawdownModel?.Income?.PaymentEnd ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.PaymentEnd;
        const pensionYearFrequency = calculateBalanceFrequency(
            transferOrDrawdown?.IncomePaymentDetails?.LastGadReviewDate,
            firstPaymentDate,
            paymentEndDate,
            frequency
        );
        if (pensionYearFrequency <= 0) {
            return 0;
        }
        const returnValue = (gadMaximum - incomePaidToDate) / pensionYearFrequency;
        return Math.floor(returnValue * 100) / 100;
    }
    return 0;
};

export const calculateMaximumIncomePaymentForRemainderOfYearForExistingArrangement = (
    processType: processTypeEnum | ProcessType,
    cappedModel: CappedModel,
    income: DrawdownAnnualIncome
) => {
    if (processType == processTypeEnum.topUpWithITFLS || processType == ProcessType.TopUpWithITFLS) {
        return cappedArrangementWithTarget(cappedModel, income);
    }
    return 0;
};

export const calculateMaximumIncomePaymentForFutureYearForExistingArrangement = (
    processType: processTypeEnum | ProcessType,
    cappedModel: CappedModel,
    income: DrawdownAnnualIncome
) => {
    if (processType == processTypeEnum.topUpWithITFLS || processType == ProcessType.TopUpWithITFLS) {
        // Gadmaximum / frequency
        const returnValue = (cappedModel?.GadMaximum || 0) / frequencyMap[income?.Frequency || Frequency.Monthly];
        return Math.floor(returnValue * 100) / 100;
    }
    return 0;
};

export const calculateMaximumIncomePaymentForFutureYear = (
    processType: processTypeEnum | ProcessType,
    transferOrDrawdown: TransferOrDrawdown,
    pensionBase: PensionBase
): number => {
    if (processType == processTypeEnum.NewBusiness || processType == ProcessType.NB) {
        // (Next Gad Maximum / Frequency)
        // TODO- next gad maximum hardcode for now
        const nextGadMaximum = transferOrDrawdown?.IncomePaymentDetails?.GadMaximum?.Amount || 0;
        const frequency =
            pensionBase?.DrawdownModel?.Income?.Frequency ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency;
        const returnValue = nextGadMaximum / frequencyMap[frequency || Frequency.Monthly];
        // round down to 2 decimals
        return Math.floor(returnValue * 100) / 100;
    }
    return 0;
};

export const calculateMaximumIncomePaymentForFlexiTarget = (
    processType: processTypeEnum | ProcessType,
    transferOrDrawdown: TransferOrDrawdown,
    pensionBase: PensionBase,
    isITFC: boolean
): number => {
    if (processType == processTypeEnum.NewBusiness || processType == ProcessType.NB) {
        // (Amount for Flexi Access Drawdown - Initial adviser charges taken from product cash)-SIPP Product charges)
        if (transferOrDrawdown?.ProviderDetails?.Name == nbSippIncomePaymentDetailsLabels.crystallisation) {
            // From drawdown
            // Amount for Flexi Access Drawdown = Net to Drawdown
            // IAC = DIAC
            const amountForFlexiAccessDrawdown = calculateNetDrawdownValue(
                pensionBase?.DrawdownModel,
                pensionBase?.PensionProtection
            );
            const IAC = calculateDIAC(pensionBase?.DrawdownModel) + calculateIAC(pensionBase, isITFC, false);
            const returnValue = amountForFlexiAccessDrawdown - IAC;
            return returnValue < 0 ? 0 : returnValue;
        } else {
            // From transfer
            // Amount for Flexi Access Drawdown = transfer value
            // IAC = Proportion of IAC
            const amountForFlexiAccessDrawdown = maximumIncomePerPaymentForFlexiTransfers([transferOrDrawdown], true);
            const IAC = calculateProportionIAC(
                maximumIncomePerPaymentForFlexiTransfers([transferOrDrawdown], false) || 0,
                pensionBase
            );
            const returnValue = amountForFlexiAccessDrawdown - IAC;
            return returnValue < 0 ? 0 : returnValue;
        }
    }
    return 0;
};

export const calculateMaximumIncomePaymentForFlexiTargetForExistingArrangement = (
    processType: processTypeEnum | ProcessType,
    flexiModel: FlexiModel,
    drawdown: DrawdownModel,
    pensionProtection: PensionProtectionDetails
) => {
    if (processType == processTypeEnum.topUpWithITFLS || processType == ProcessType.TopUpWithITFLS) {
        if (flexiModel?.From == "Crystallisation") {
            // From drawdown
            // Amount for Flexi Access Drawdown = Net to Drawdown
            // IAC = DIAC
            const amountForFlexiAccessDrawdown = calculateNetDrawdownValue(drawdown, pensionProtection);
            const IAC = calculateDIAC(drawdown);
            const returnValue = amountForFlexiAccessDrawdown - IAC;
            return returnValue < 0 ? 0 : returnValue;
        } else {
            // From existing arrangement
            // Amount for Flexi Access Drawdown = existing amount - current income
            const result = calculateFlexiArrangementAmount(flexiModel);
            return result < 0 ? 0 : result;
        }
    }
    // TODO: handle other process types
};

// used for calculating gross one-off income payment
const sumGadMaximumForCappedTransfers = (cappedTransfers: PensionTransfer[]) => {
    let total = 0;
    cappedTransfers.forEach((e) => {
        const value =
            (e?.IncomePaymentDetails?.GadMaximum?.Amount || 0) -
            (e?.IncomePaymentDetails?.CurrentYearIncome?.Amount || 0);
        total += value;
    });
    return total;
};

const cappedTransferWithTarget = (
    transfers: PensionTransfer[],
    firstPaymentDate: IJsonDate,
    paymentEndDate: IJsonDate,
    frequency: Frequency
) => {
    let total = 0;
    transfers = transfers || [];
    firstPaymentDate = firstPaymentDate || JsonDateParser.toJsonDateNoOffset(new Date());
    frequency = frequency || Frequency.Monthly;
    transfers.forEach((e) => {
        const balanceFrequency = calculateBalanceFrequency(
            e?.IncomePaymentDetails?.LastGadReviewDate,
            firstPaymentDate,
            paymentEndDate,
            frequency
        );
        if (balanceFrequency > 0) {
            let value =
                ((e?.IncomePaymentDetails?.GadMaximum?.Amount || 0) -
                    (e?.IncomePaymentDetails?.CurrentYearIncome?.Amount || 0)) /
                balanceFrequency;

            // round down to 2 decimals
            value = Math.floor(value * 100) / 100;
            total += value;
        }
    });
    return total;
};

const cappedArrangementWithTarget = (cappedModel: CappedModel, income: DrawdownAnnualIncome): number => {
    // SUM ((((Each existing GAD Maximum - Each income paid to date) - each in progress capped income payments)/ Pension Year Frequency)
    const gadMaximum = cappedModel?.GadMaximum || 0;
    const incomePaidToDate = cappedModel?.IncomeTakenToDate || 0;
    const inProgressIncome = cappedModel?.IncomePerPaymentForCurrentPensionYear || 0;
    const frequencyBalance = calculateBalanceFrequency(
        cappedModel?.LastMandatoryReview,
        income?.NextPayment,
        income?.PaymentEnd,
        income?.Frequency
    );

    if (frequencyBalance <= 0) {
        return 0;
    }
    const returnValue = (gadMaximum - incomePaidToDate - inProgressIncome) / frequencyBalance;
    if (returnValue < 0) {
        return 0;
    } else {
        return Math.floor(returnValue * 100) / 100;
    }
};

const maximumIncomePerPaymentForCappedTransfers = (
    cappedTransfers: PensionTransfer[],
    frequency: Frequency
): number => {
    let total = 0;
    cappedTransfers = cappedTransfers || [];
    // Maximum Income Per Payment = (SUM (Each Transfer GAD Maximum/Frequency)
    cappedTransfers.forEach((e) => {
        const value = (e?.IncomePaymentDetails?.GadMaximum?.Amount || 0) / frequencyMap[frequency || Frequency.Monthly];
        total += value;
    });
    return total;
};

export const maximumIncomePerPaymentForFlexiTransfers = (
    flexiTransfers: PensionTransfer[],
    needResidualCash = false
): number => {
    // Maximum Income Per Payment = (SUM(Each Transfer Cash Value))
    let total = 0;
    flexiTransfers = flexiTransfers || [];
    flexiTransfers.forEach((e) => {
        total += e?.CurrentValue?.Amount || 0;
        if (e?.Instruments != null && e?.Instruments?.length > 0) {
            // calculate re-reg value
            e?.Instruments?.forEach((el) => {
                total += (el?.Units || 0) * (el?.Investment?.UnitPrice?.Amount || 0);
            });
            total += needResidualCash ? e?.ReRegisterValue?.Amount || 0 : 0;
        }
    });
    return total;
};

/**
 *
 * @param data PensionBase model
 * @param isITFC if true - will inclue pre-retire transfer re-reg value
 * @param includePostFlexi if true - will include post-retire transfer value (both cash * re-reg)
 * @returns
 */
const calculateIAC = (data: PensionBase, isITFC: boolean, includePostFlexi = true) => {
    // single payment + pre-retirement cash transfer
    let preTotalCash = totalCashValue(data);
    // Not itfc - add pre-retirement re-reg transfer
    preTotalCash += isITFC ? 0 : totalReRegValue(data);
    // add post-retirement flexi transfer (both cash & rereg)
    preTotalCash += includePostFlexi
        ? maximumIncomePerPaymentForFlexiTransfers(
              data?.PensionTransfers?.filter(
                  (e) =>
                      e?.PensionSource == PensionArrangementType.Crystallised &&
                      e?.DrawdownType == DrawdownType.Flexible
              )
          )
        : 0;
    const totalContribution = ChargesHelper.getTotalForIACEstimation(data, SubAccountType.Npr);
    return data?.InitialAdviserCharge?.SourceType == SourceType.WrapCash
        ? 0
        : data?.InitialAdviserCharge?.Type == MoneyOrPercentageType.Money
        ? (preTotalCash * (data?.InitialAdviserCharge?.Money?.Amount || 0)) / totalContribution
        : ((data?.InitialAdviserCharge?.Percentage || 0) * preTotalCash) / 100;
};

/**
 *
 * @param transferAmount current transfer amount
 * @param pensionBase pensionBase model
 * @returns Return de proportion iac value again the total transfer value (include pre/post retire transfer - both cash & re-reg)
 */
const calculateProportionIAC = (transferAmount: number, pensionBase: PensionBase) => {
    const iacType = pensionBase?.TransferInitialAdviceCharge?.Type;
    if (iacType == MoneyOrPercentageType.Money) {
        let totalTransfer = 0;
        pensionBase?.PensionTransfers?.forEach((e) => {
            totalTransfer += e?.CurrentValue?.Amount || 0;
        });
        return (transferAmount / totalTransfer) * (pensionBase?.TransferInitialAdviceCharge?.Money?.Amount || 0);
    } else {
        return (transferAmount * pensionBase?.TransferInitialAdviceCharge?.Percentage) / 100;
    }
};

/**
 *
 * @param drawdown
 * @returns Return DIAC (cash or percetage)
 */
const calculateDIAC = (drawdown: DrawdownModel) => {
    if (drawdown == null) {
        return 0;
    }
    return drawdown?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.SourceType == SourceType.WrapCash
        ? 0
        : drawdown?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Type == MoneyOrPercentageType.Money
        ? drawdown?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Money?.Amount || 0
        : ((drawdown?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Percentage || 0) *
              drawdown?.CrystallisationDetails?.CrystallisationAmount?.Amount) /
          100;
};

/**
 *
 * @param flexiModel Existing flexi arrangement
 * @returns value of existing flexi access arrangements - any in progress income payments
 */
const calculateFlexiArrangementAmount = (flexiModel: FlexiModel) => {
    // TODO - need to determine how to calculate in progress income payments
    return (flexiModel.Amount || 0) - (flexiModel?.IncomePerPayment || 0);
};

const hasCappedTransferOnly = (pensionTransfers: PensionTransfer[]): boolean => {
    pensionTransfers = pensionTransfers || [];
    return !pensionTransfers.some((e) => e.DrawdownType == DrawdownType.Flexible);
};

const hasCappedTransfer = (pensionTransfers: PensionTransfer[]): boolean => {
    pensionTransfers = pensionTransfers || [];
    return pensionTransfers.some((e) => e.DrawdownType == DrawdownType.Capped);
};

const hasFlexiTransferOnly = (pensionTransfers: PensionTransfer[]): boolean => {
    pensionTransfers = pensionTransfers || [];
    return !pensionTransfers.some((e) => e.DrawdownType == DrawdownType.Capped);
};

const calculateBalanceFrequency = (
    lastGadReviewDate: IJsonDate,
    firstPaymentDate: IJsonDate,
    paymentEndDate: IJsonDate,
    frequency: Frequency
): number => {
    const frequencyMap = {
        [Frequency.Monthly]: 1,
        [Frequency.Quarterly]: 3,
        [Frequency.HalfYearly]: 6,
        [Frequency.Yearly]: 12,
    };
    if (lastGadReviewDate == null || firstPaymentDate == null) {
        return 0;
    }
    const yearEnd =
        paymentEndDate == null
            ? addDays(addYears(JsonDateParser.toDate(lastGadReviewDate), 1), -1)
            : addYears(JsonDateParser.toDate(firstPaymentDate), 1) <= JsonDateParser.toDate(paymentEndDate)
            ? addDays(addYears(JsonDateParser.toDate(lastGadReviewDate), 1), -1)
            : JsonDateParser.toDate(paymentEndDate);
    const pensionYearEnd = findPensionYearEnd(JsonDateParser.toDate(lastGadReviewDate), yearEnd);
    const monthDifference = DateHelper.getMonthDifference(pensionYearEnd, JsonDateParser.toDate(firstPaymentDate));
    return monthDifference < 0
        ? 0
        : frequency == Frequency.Monthly
        ? monthDifference + 1
        : (monthDifference + 1) % frequencyMap[frequency] == 0
        ? (monthDifference + 1) / frequencyMap[frequency]
        : Math.floor((monthDifference + 1) / frequencyMap[frequency]) + 1;
};

const findPensionYearEnd = (pensionYearStart: Date, pensionYearEnd: Date): Date => {
    const nowDate = new Date();
    if (nowDate >= pensionYearStart && nowDate <= pensionYearEnd) {
        return pensionYearEnd;
    } else {
        const newStartDate = addDays(pensionYearEnd, 1);
        const newEndDate = addYears(pensionYearEnd, 1);
        return findPensionYearEnd(newStartDate, newEndDate);
    }
};

export const frequencyMap = {
    [Frequency.Monthly]: 12,
    [Frequency.Quarterly]: 4,
    [Frequency.HalfYearly]: 2,
    [Frequency.Yearly]: 1,
};
